import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  ExternalLink,
  FileText,
  Filter,
  Loader2,
  LogOut,
  Mail,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { AdminEligibility, tokenStorage } from "@/lib/api";

/**
 * Compliance-friendly log of every Eligibility Letter that has been
 * generated (and optionally sent) across all eligibility checks. Admins can
 * filter by date range + free-text applicant name / reference no. and can
 * re-download any letter by clicking the row action.
 */
export default function AdminLettersIssued() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(monthAgo);
  const [toDate, setToDate] = useState(today);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200, status };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (q.trim()) params.q = q.trim();
      const r = await AdminEligibility.listIssuedLetters(params);
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      if (e?.response?.status === 401) {
        tokenStorage.clear();
        navigate("/admin", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, status, q, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const download = async (checkId, ref) => {
    setDownloadingId(checkId);
    try {
      const url = await AdminEligibility.aiPdfBlobUrl(checkId);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sahyog-eligibility-${(ref || checkId).replace(/[^\w-]+/g, "_")}.pdf`;
      a.click();
    } catch (e) {
      alert(e?.response?.data?.detail || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  };

  const logout = () => {
    tokenStorage.clear();
    navigate("/admin", { replace: true });
  };

  const stats = useMemo(() => {
    const generated = items.length;
    const sent = items.filter((x) => x.ai_report?.sent_to_customer_at).length;
    return { generated, sent };
  }, [items]);

  return (
    <div className="min-h-screen bg-[#0A1F44] text-white">
      <div className="border-b border-white/10 bg-black/30 sticky top-0 z-30 backdrop-blur-lg">
        <div className="max-w-[1440px] mx-auto flex items-center gap-3 px-6 py-3">
          <Link
            to="/admin/eligibility-checks"
            className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white/90"
          >
            <ArrowLeft size={13} /> Back to Eligibility Checks
          </Link>
          <div className="flex-1" />
          <button
            onClick={load}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 hover:border-white/40 text-xs text-white/70"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            onClick={logout}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-rose-400/30 hover:border-rose-400/60 text-xs text-rose-200"
          >
            <LogOut size={12} /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 py-6">
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-1.5">
            Compliance Log
          </div>
          <h1 className="font-display text-3xl text-white mb-1 flex items-center gap-2">
            <FileText className="text-[#F0D788]" size={26} />
            Letters Issued
          </h1>
          <p className="text-sm text-white/60 max-w-2xl">
            Every Preliminary Loan Eligibility Letter that has been generated for a
            customer, including who sent it, when, and to which applicant. Re-download
            any letter from the row action for audit or a re-send.
          </p>
        </div>

        {/* Filter row */}
        <div className="rounded-xl border border-white/12 bg-white/[0.03] p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="block text-[9px] uppercase tracking-wider text-white/45 mb-1">
                From
              </label>
              <input
                type="date"
                data-testid="letters-filter-from"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full bg-black/30 border border-white/15 rounded-md px-2 py-1.5 text-xs text-white"
              />
            </div>
            <div>
              <label className="block text-[9px] uppercase tracking-wider text-white/45 mb-1">
                To
              </label>
              <input
                type="date"
                data-testid="letters-filter-to"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full bg-black/30 border border-white/15 rounded-md px-2 py-1.5 text-xs text-white"
              />
            </div>
            <div>
              <label className="block text-[9px] uppercase tracking-wider text-white/45 mb-1">
                Status
              </label>
              <select
                data-testid="letters-filter-status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full bg-black/30 border border-white/15 rounded-md px-2 py-1.5 text-xs text-white"
              >
                <option value="all">All (generated + sent)</option>
                <option value="sent">Sent to customer only</option>
                <option value="generated_only">Generated but not sent</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[9px] uppercase tracking-wider text-white/45 mb-1">
                Search
              </label>
              <div className="relative">
                <input
                  type="text"
                  data-testid="letters-filter-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  placeholder="Applicant name or SF/NIAM/EL/… reference"
                  className="w-full bg-black/30 border border-white/15 rounded-md pl-8 pr-2 py-1.5 text-xs text-white placeholder:text-white/30"
                />
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="text-[11px] text-white/50 flex items-center gap-3">
              <span data-testid="letters-count-total">
                <Filter size={10} className="inline mr-1" /> {total} letters in window
              </span>
              <span data-testid="letters-count-sent" className="text-emerald-300/80">
                <Mail size={10} className="inline mr-1" /> {stats.sent} sent to customer
              </span>
            </div>
            <button
              onClick={load}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/50 hover:border-[#D4AF37] text-[11px] text-[#F0D788] font-semibold"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-white/12 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.04] text-[11px] uppercase tracking-wider text-white/55">
                <tr>
                  <th className="text-left px-4 py-3">Applicant</th>
                  <th className="text-left px-4 py-3">Reference No.</th>
                  <th className="text-left px-4 py-3">Generated</th>
                  <th className="text-left px-4 py-3">Sent</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/8">
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-white/55">
                      <Loader2 className="inline animate-spin mr-2" size={13} /> Loading…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-white/55">
                      No letters found for the selected filters.
                    </td>
                  </tr>
                )}
                {!loading &&
                  items.map((it, i) => {
                    const r = it.ai_report || {};
                    const sent = !!r.sent_to_customer_at;
                    return (
                      <tr
                        key={it.id}
                        data-testid={`letters-row-${i}`}
                        className="hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="text-white font-medium">{it.name}</div>
                          <div className="text-[11px] text-white/50">
                            {it.loan_type_label || it.loan_type}
                          </div>
                          <div className="text-[10px] text-white/40 font-mono mt-0.5">
                            Lead: {it.id.slice(0, 8).toUpperCase()}
                          </div>
                          {it.deleted_at && (
                            <div className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-rose-400/40 bg-rose-500/10 text-rose-200 text-[9px] uppercase tracking-wider mt-1">
                              Deleted check
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-[11px] font-mono text-[#F0D788]">
                            {r.reference_no}
                          </div>
                          <div className="text-[10px] text-white/40 mt-0.5">
                            {r.filename}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[11px] text-white/75">
                          <div>{fmtDate(r.generated_at)}</div>
                          {r.generated_by && (
                            <div className="text-[10px] text-white/45">by {r.generated_by}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[11px]">
                          {sent ? (
                            <>
                              <div className="text-emerald-300">
                                {fmtDate(r.sent_to_customer_at)}
                              </div>
                              {r.sent_to_customer_by && (
                                <div className="text-[10px] text-white/45">
                                  by {r.sent_to_customer_by}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-white/45">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${
                              sent
                                ? "border-emerald-400/50 bg-emerald-500/10 text-emerald-200"
                                : "border-amber-400/50 bg-amber-500/10 text-amber-200"
                            }`}
                          >
                            {sent ? "Sent" : "Generated"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            data-testid={`letters-download-${i}`}
                            onClick={() => download(it.id, r.reference_no)}
                            disabled={downloadingId === it.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#D4AF37]/50 hover:border-[#D4AF37] text-[11px] text-[#F0D788] mr-1.5 disabled:opacity-40"
                          >
                            {downloadingId === it.id ? (
                              <Loader2 size={11} className="animate-spin" />
                            ) : (
                              <Download size={11} />
                            )}
                            PDF
                          </button>
                          <Link
                            to={`/admin/eligibility-checks?open=${it.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 hover:border-white/40 text-[11px] text-white/70"
                          >
                            <ExternalLink size={11} /> Open
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
